import { Preferences } from '@capacitor/preferences';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import api, { triggerProactiveRefreshCheck } from './api/client';
import { queryClient } from '../lib/queryClient';

// ─── SHA-256 helper ──────────────────────────────────────────────────────────

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PREF_ENABLED = 'helm_lock_enabled';
const PREF_IDLE_TIMEOUT = 'helm_lock_idle_timeout';
const PREF_BG_TS = 'helm_lock_bg_ts';
const PREF_AUDIT_QUEUE = 'helm_lock_audit_queue';
const PREF_PIN_SALT = 'helm_pin_salt';

const BIOMETRIC_SERVER = 'helmapp.co';
const BIOMETRIC_USERNAME = 'helm_pin_credential';

export const IDLE_TIMEOUT_OPTIONS = [
  { label: 'Immediately', value: 0 },
  { label: '1 minute', value: 60_000 },
  { label: '5 minutes', value: 300_000 },
  { label: '15 minutes', value: 900_000 },
  { label: '30 minutes', value: 1_800_000 },
  { label: '1 hour', value: 3_600_000 },
  { label: 'Never', value: -1 },
];

export type AppLockState = {
  enabled: boolean;
  idleTimeout: number;
  lastUnlockAt: number | null;
};

// ─── AppLockService ──────────────────────────────────────────────────────────

export class AppLockService {
  private safewordCacheTs = 0;
  private safewordCacheValue = false;
  private _initialized = false;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  async init(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    if (this._initialized) return;
    this._initialized = true;

    App.addListener('appStateChange', async ({ isActive }) => {
      if (!isActive) {
        // Going to background — record timestamp
        await Preferences.set({
          key: PREF_BG_TS,
          value: String(Date.now()),
        });
      } else {
        // Returning to foreground.
        // Spec 284: proactive refresh check runs UNCONDITIONAL of App Lock
        // enablement — it must NOT live inside onForeground(), which
        // early-returns before doing anything when App Lock is disabled
        // (the majority of users). The refresh check and the (separate,
        // unrelated) App-Lock decision of whether to show the biometric
        // prompt both react to this same appStateChange event but are
        // independent — not sequenced on one another. Fire-and-forget:
        // triggerProactiveRefreshCheck() already fails closed internally.
        triggerProactiveRefreshCheck();
        await this.onForeground();
      }
    });
  }

  // ── Foreground handler (CRITICAL safety gate) ──────────────────────────────

  private async onForeground(): Promise<void> {
    await this.checkRecoveryPending();

    // SAFETY INVARIANT: Never lock during an active safeword
    const safewordActive = await this.getSafewordStatus();
    if (safewordActive) return;

    const enabledVal = (await Preferences.get({ key: PREF_ENABLED })).value;
    if (enabledVal !== 'true') return;

    const bgTs = (await Preferences.get({ key: PREF_BG_TS })).value;
    const elapsed = Date.now() - Number(bgTs ?? '0');
    const idleTimeout = await this.getIdleTimeout();

    if (idleTimeout !== -1 && elapsed >= idleTimeout) {
      this.activateLockScreen();
    }
  }

  // ── Lock screen events ─────────────────────────────────────────────────────

  activateLockScreen(): void {
    window.dispatchEvent(new CustomEvent('helm:lock-activate'));
  }

  deactivateLockScreen(): void {
    window.dispatchEvent(new CustomEvent('helm:lock-deactivate'));
  }

  // ── State ──────────────────────────────────────────────────────────────────

  async getState(): Promise<AppLockState> {
    const [enabledVal, timeoutVal] = await Promise.all([
      Preferences.get({ key: PREF_ENABLED }),
      Preferences.get({ key: PREF_IDLE_TIMEOUT }),
    ]);

    return {
      enabled: enabledVal.value === 'true',
      idleTimeout: timeoutVal.value != null ? Number(timeoutVal.value) : 300_000,
      lastUnlockAt: null,
    };
  }

  async enable(idleTimeout: number): Promise<void> {
    await Promise.all([
      Preferences.set({ key: PREF_ENABLED, value: 'true' }),
      Preferences.set({ key: PREF_IDLE_TIMEOUT, value: String(idleTimeout) }),
    ]);
  }

  async disable(): Promise<void> {
    await this.clearAppLockCredentials();
    window.dispatchEvent(new CustomEvent('helm:lock-disable'));
  }

  async getIdleTimeout(): Promise<number> {
    const { value } = await Preferences.get({ key: PREF_IDLE_TIMEOUT });
    return value != null ? Number(value) : 300_000;
  }

  // ── PIN management ─────────────────────────────────────────────────────────

  async setPin(pin: string): Promise<void> {
    // Generate a 16-byte hex salt
    const saltBytes = new Uint8Array(16);
    crypto.getRandomValues(saltBytes);
    const salt = Array.from(saltBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    const hash = await sha256Hex(pin + salt);

    await Preferences.set({ key: PREF_PIN_SALT, value: salt });

    const { NativeBiometric } = await import('capacitor-native-biometric');
    await NativeBiometric.setCredentials({
      server: BIOMETRIC_SERVER,
      username: BIOMETRIC_USERNAME,
      password: hash,
    });
  }

  async verifyPin(pin: string): Promise<boolean> {
    try {
      const { value: salt } = await Preferences.get({ key: PREF_PIN_SALT });
      if (!salt) return false;

      const hash = await sha256Hex(pin + salt);

      const { NativeBiometric } = await import('capacitor-native-biometric');
      const cred = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
      return cred.password === hash;
    } catch {
      return false;
    }
  }

  // ── Biometric authentication ───────────────────────────────────────────────

  async authenticateWithBiometric(reason: string): Promise<boolean> {
    try {
      const { NativeBiometric } = await import('capacitor-native-biometric');
      await NativeBiometric.verifyIdentity({ reason, title: 'Unlock Helm' });
      return true;
    } catch {
      return false;
    }
  }

  // ── Credential cleanup ─────────────────────────────────────────────────────

  async clearAppLockCredentials(): Promise<void> {
    try {
      const { NativeBiometric } = await import('capacitor-native-biometric');
      await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
    } catch {
      // Ignore — credentials may not exist
    }

    await Promise.all([
      Preferences.remove({ key: PREF_PIN_SALT }),
      Preferences.remove({ key: PREF_BG_TS }),
      Preferences.remove({ key: PREF_ENABLED }),
    ]);
  }

  // Returns false if a safeword is active — callers must NOT lock in that case.
  async canLockNow(): Promise<boolean> {
    const safewordActive = await this.getSafewordStatus();
    return !safewordActive;
  }

  // ── Safeword cache invalidation ───────────────────────────────────────────

  invalidateSafewordCache(): void {
    this.safewordCacheTs = 0;
  }

  // ── Safeword status (60s TTL cache, fail-open) ────────────────────────────

  private async getSafewordStatus(): Promise<boolean> {
    const now = Date.now();
    if (now - this.safewordCacheTs < 60_000) {
      return this.safewordCacheValue;
    }

    try {
      const resp = await api.get('/safeword/status');
      const active: boolean = resp.data?.data?.active ?? resp.data?.active ?? false;
      this.safewordCacheTs = Date.now();
      this.safewordCacheValue = active;
      return active;
    } catch {
      // FAIL OPEN — never block safeword access due to network error
      return false;
    }
  }

  // ── Recovery pending check ────────────────────────────────────────────────

  private async checkRecoveryPending(): Promise<void> {
    try {
      const resp = await api.get('/users/me/app-lock-recovery-pending');
      const pending: boolean =
        resp.data?.data?.recoveryPending ?? resp.data?.recoveryPending ?? false;

      if (pending) {
        await this.clearAppLockCredentials();
        await api.post('/users/me/app-lock-recovery-pending/acknowledge');
        window.dispatchEvent(new CustomEvent('helm:lock-disable'));
        queryClient.invalidateQueries({ queryKey: ['mobile-privacy'] });
      }
    } catch {
      // Ignore — not critical path
    }
  }

  // ── Audit logging ──────────────────────────────────────────────────────────

  async logAuditEvent(event: string): Promise<void> {
    try {
      await api.post('/users/me/app-lock-events', { event });
    } catch {
      // Queue for later flush
      try {
        const { value: raw } = await Preferences.get({ key: PREF_AUDIT_QUEUE });
        const queue: string[] = raw ? JSON.parse(raw) : [];
        queue.push(event);
        await Preferences.set({ key: PREF_AUDIT_QUEUE, value: JSON.stringify(queue) });
      } catch {
        // Ignore storage errors
      }
    }
  }

  async flushAuditQueue(): Promise<void> {
    try {
      const { value: raw } = await Preferences.get({ key: PREF_AUDIT_QUEUE });
      if (!raw) return;
      const queue: string[] = JSON.parse(raw);
      if (!queue.length) return;
      await api.post('/users/me/app-lock-events', { events: queue });
      await Preferences.remove({ key: PREF_AUDIT_QUEUE });
    } catch {
      // Will retry on next successful unlock
    }
  }
}

// ─── Singleton ────────────────────────────────────────────────────────────────

let _instance: AppLockService | null = null;

export function getAppLockService(): AppLockService {
  if (!_instance) {
    _instance = new AppLockService();
  }
  return _instance;
}
